import { useEffect, useState } from "react";
import { Alert, Button, Icon, Modal, Spinner, TextInput, Typography } from "@/components/atoms";
import { Map } from "@/components/atoms/Map/Map";
import { FormBottomSheet } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useCurrentLocation } from "@/hooks/useCurrentLocation";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { snapToNearestRoad } from "@/lib/googleRoads";
import { useAddClientPoiMutation, useUpdateClientPoiMutation } from "@/redux/apis/clientPoi/clientPoiApi";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { addToast, clsx, showSnackbar } from "@/utils";
import { categoryToRmTags, getLocationCategory, LocationCategory } from "../helpers";
import { LatLng, geocoderResultToPoi } from "../suggestions";
import { LocationAddressAutocomplete } from "./LocationAddressAutocomplete";

const CATEGORY_OPTIONS: { value: LocationCategory; label: string; icon: string }[] = [
  { value: "home", label: "Home", icon: "Home" },
  { value: "work", label: "Work", icon: "Briefcase" },
  { value: "custom", label: "Custom", icon: "Star1" },
];

interface AddEditLocationModalProps {
  open: boolean;
  mode: "add" | "edit";
  location?: PointsOfInterest;
  accountId?: string;
  onClose: () => void;
  // "Save to Favourites" variant (e.g. from the booking sheet): hides the Home/Work/Custom selector
  // + airport tip, starts the name blank, and lets the caller override the copy. Saves as Custom/Other.
  hideCategory?: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
}

export const AddEditLocationModal = ({
  open,
  mode,
  location,
  accountId,
  onClose,
  hideCategory = false,
  title,
  description,
  submitLabel,
}: AddEditLocationModalProps) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState<LatLng | null>(null);
  const [selected, setSelected] = useState<PointsOfInterest | null>(null);
  const [category, setCategory] = useState<LocationCategory>("home");

  const currentLocation = useCurrentLocation();
  const isMdUp = useIsMdUp();

  const [addPoi, { isLoading: isAdding }] = useAddClientPoiMutation();
  const [updatePoi, { isLoading: isUpdating }] = useUpdateClientPoiMutation();
  const isLoading = isAdding || isUpdating;

  useEffect(() => {
    if (!open) return;
    // Save-to-Favourites starts the name blank (user names it); favourites add/edit prefills it.
    setName(hideCategory ? "" : location?.rm_label ?? "");
    setAddress(location?.address ?? "");
    setCoords(location ? { lat: location.lat, lng: location.lng } : null);
    setSelected(location ?? null);
    setCategory(hideCategory ? "custom" : location ? getLocationCategory(location) : "home");
  }, [open, location, hideCategory]);

  const handleSelect = (poi: PointsOfInterest) => {
    setSelected(poi);
    setAddress(poi.address);
    setCoords({ lat: poi.lat, lng: poi.lng });
  };

  // Dragging the map pin reverse-geocodes the new position (Google Geocoder) so the address and
  // required POI fields stay in sync — the web equivalent of the mobile pin-location screen.
  const handlePinChange = async (next: LatLng) => {
    // Snap to the nearest road first (mobile parity), then reverse-geocode the snapped point.
    const snapped = await snapToNearestRoad(next);
    setCoords(snapped);
    if (!window.google?.maps?.Geocoder) return;
    new window.google.maps.Geocoder().geocode({ location: snapped }, (results, status) => {
      if (status === "OK" && results?.[0]) handleSelect(geocoderResultToPoi(results[0], snapped));
    });
  };

  const canSubmit = Boolean(name.trim() && address.trim() && coords);

  const handleSubmit = () => {
    if (!coords) return;
    // Carry forward address metadata (city/postcode/state/vendor) from the selected POI, but never
    // its uuid — the add branch must create a fresh POI, and the edit branch sets the uuid explicitly.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { uuid: _selectedUuid, ...carriedFields } = selected ?? ({} as Partial<PointsOfInterest>);
    const body = {
      ...carriedFields,
      rm_label: name.trim(),
      address: address.trim(),
      lat: coords.lat,
      lng: coords.lng,
      // Saved client POIs are favourites; the backend requires a non-empty rm_type (mobile parity).
      rm_type: "FAVOURITE",
      // The "Save as" selection (Home/Work/Custom) maps to the backend rm_tags value (mobile parity:
      // location_home / location_work / location_other). Overrides any tags carried from the autocomplete pick.
      rm_tags: categoryToRmTags(category),
    };
    const action = mode === "edit" && location ? updatePoi({ ...body, uuid: location.uuid, accountId }) : addPoi({ ...body, accountId });
    action
      .unwrap()
      .then(() => {
        showSnackbar({
          size: "small",
          duration: 5000,
          message: `Successfully ${mode === "edit" ? "updated" : "added"} location ${name.trim()}`,
        });
        onClose();
      })
      .catch((error) => addToast("danger", getErrorMessages(error)[0]));
  };

  const resolvedTitle = title ?? (mode === "edit" ? "Edit Location" : "Add Location");
  const resolvedDescription = description ?? "Save frequently visited locations for faster and easier bookings";
  const resolvedSubmitLabel = submitLabel ?? (mode === "edit" ? "Save" : "Add Location");
  // While the add/update mutation is in flight the submit shows a spinner + "Saving…" (the bottom
  // sheet, whose button we don't render directly, gets the label swap).
  const submitText = isLoading ? "Saving…" : resolvedSubmitLabel;

  const formBody = (
    <div className="flex flex-col gap-5">
      {!hideCategory && (
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Save as
          </Typography>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((option) => {
              const isActive = category === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setCategory(option.value)}
                  className={clsx(
                    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2",
                    isActive
                      ? "border-primary bg-primary-light text-primary-dark"
                      : "border-neutral-mid-gray text-neutral-dark-gray hover:bg-neutral-gray"
                  )}
                >
                  <Icon name={option.icon} size={18} className={isActive ? "text-primary-dark" : "text-neutral-dark-gray"} />
                  <Typography variant="action" className={isActive ? "text-primary-dark" : "text-neutral-dark-gray"}>
                    {option.label}
                  </Typography>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <label htmlFor="favourite-location-name">
          <Typography variant="action" className="text-neutral-dark-gray">
            Location Name
          </Typography>
        </label>
        <TextInput id="favourite-location-name" placeholder="Enter Location Name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {/* Address, the airport tip, and the map are grouped at 8px so the alert sits 8px from
            the field above and the map below (the rest of the modal is spaced at gap-5). */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Address
          </Typography>
          <LocationAddressAutocomplete value={address} onChange={setAddress} onSelect={handleSelect} userLocation={currentLocation} />
        </div>
        {!hideCategory && (
          <Alert
            type="info"
            className="border border-info"
            message="Skip the airport terminal when saving a flight location — adding your flight number at booking updates the terminal automatically."
          />
        )}
        {/* data-vaul-no-drag: pin-dragging the map inside the mobile sheet must not swipe-dismiss it. */}
        <div data-vaul-no-drag className="h-[277px] w-full overflow-hidden rounded-lg bg-neutral-surface-gray">
          <Map pinMode center={coords ?? currentLocation} zoom={coords || currentLocation ? 15 : undefined} onPinChange={handlePinChange} />
        </div>
      </div>
    </div>
  );

  if (!isMdUp)
    return (
      <FormBottomSheet
        open={open}
        onClose={onClose}
        title={resolvedTitle}
        description={resolvedDescription}
        submitLabel={submitText}
        onSubmit={handleSubmit}
        submitDisabled={!canSubmit || isLoading}
        cancelDisabled={isLoading}
      >
        {formBody}
      </FormBottomSheet>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-medium"
      title={resolvedTitle}
      description={resolvedDescription}
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            startIcon={isLoading ? <Spinner variant="light" /> : undefined}
          >
            {submitText}
          </Button>
        </>
      )}
    >
      {formBody}
    </Modal>
  );
};
